import './BodyTooltip.scss';
import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface BodyTooltipPosition {
  x: number;
  y: number;
}

interface BodyTooltipProps {
  showTooltip: boolean | undefined;
  children: ReactNode;
  position?: BodyTooltipPosition | undefined;
}

function BodyTooltip({ showTooltip = false, children, position }: BodyTooltipProps) {
  const el = useMemo(() => document.createElement('div'), []);
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    document.body.appendChild(el);
    return () => {
      document.body.removeChild(el);
    };
  }, [el]);

  useLayoutEffect(() => {
    if (ref.current && showTooltip) {
      const rect = ref.current.getBoundingClientRect();
      setOffset({
        x: rect.width / 2,
        y: rect.height, // Full height plus the gap
      });
    }
  }, [showTooltip, children]);

  if (!showTooltip) {
    return null;
  }

  return (
    <>
      {createPortal(
        <div
          ref={ref}
          style={{
            position: 'fixed',
            left: (position?.x ?? 0) - offset.x,
            top: (position?.y ?? 0) - offset.y,
            zIndex: 9999,
            pointerEvents: 'none',
          }}
          className="body-tooltip"
        >
          {children}
        </div>,
        el
      )}
    </>
  );
}

export default BodyTooltip;
